import {
  Autocomplete,
  AutocompleteInputChangeReason,
  OutlinedTextFieldProps,
  Popper,
  TextField,
} from "@mui/material";
import { Box } from "@mui/system";
import { useCallback, useState } from "react";
import getCompanySearch, {
  OverviewResponseProperties,
} from "../../network/get-company-search";
import {
  ControllerRenderProps,
  FieldError,
  UseFormSetError,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { debounce } from "throttle-debounce";
import config from "../../config";

type CompanySearchAutocompleteProps = {
  field: ControllerRenderProps<CompanyInformation, "searchString">;
  setError: UseFormSetError<CompanyInformation>;
  error?: FieldError;
  selectedCountry: string;
  disabled?: boolean;
};

const NUMERIC_REGEX = /^\d+(-\d+)?$/;

const CompanySearchAutocomplete = ({
  field,
  setError,
  disabled,
  error,
  selectedCountry,
}: CompanySearchAutocompleteProps) => {
  const { t } = useTranslation();
  const [searchResult, setSearchResult] = useState<
    OverviewResponseProperties | undefined
  >(undefined);

  const searchByNameEnabled =
    config.ALLOWED_COUNTRIES.includes(selectedCountry);

  const doCompanyNameSearch = useCallback(
    async (searchString: string | undefined, country: string) => {
      if (!searchString) {
        setSearchResult(undefined);
        return;
      }
      if (searchString.length < 3) {
        setSearchResult(undefined);
        return;
      }
      try {
        const searchRes = await getCompanySearch(country, searchString);
        setSearchResult(searchRes?.properties);
      } catch (error) {
        const message = t(
          NUMERIC_REGEX.test(searchString || "")
            ? "step.companyInformation.feedback.companyNotFound"
            : "step.companyInformation.companyName.feedback.companyNotFound"
        );
        setError("searchString", {
          message,
        });
        console.error("Error during company search:", error);
        setSearchResult(undefined);
      }
    },
    [setError, t]
  );

  const debouncedSearch = debounce(300, (value: string | undefined) => {
    void doCompanyNameSearch(value, selectedCountry);
  });

  const onChange = (newValue: string | CompanyOption) => {
    if (typeof newValue === "string") {
      field.onChange({ id: "", label: newValue });
    } else if (newValue?.id) {
      field.onChange(newValue);
    }
  };

  const onInputChange = (
    newValue: string,
    type: AutocompleteInputChangeReason
  ) => {
    if (type !== "reset") {
      onChange(newValue);
      if (searchByNameEnabled) {
        if (newValue && newValue.length >= 3) {
          debouncedSearch(newValue);
        } else {
          setSearchResult(undefined);
        }
      }
    }
  };
  const options =
    searchResult?.hits.map((hit) => ({
      id: hit.companyId,
      label: hit.companyName,
    })) || [];

  return (
    <Box sx={{ height: 96 }}>
      <Autocomplete
        freeSolo
        fullWidth
        disabled={disabled}
        disableClearable
        options={options}
        PopperComponent={(props) =>
          searchResult ? (
            <Popper
              modifiers={[
                {
                  name: "flip",
                  enabled: false,
                },
              ]}
              {...props}
              placement="bottom-start"
              sx={
                searchResult.hits.length > 0
                  ? {
                      border: "1px solid #1B5048",
                      borderRadius: "14px",
                    }
                  : undefined
              }
            />
          ) : null
        }
        inputValue={field.value?.label || ""}
        onInputChange={(_, newValue, type) => onInputChange(newValue, type)}
        onChange={(_, newValue) => onChange(newValue)}
        filterOptions={(options) => options}
        renderOption={(props, option) => {
          return (
            <li {...props} key={option.id}>
              {option.label}
            </li>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...(params as unknown as OutlinedTextFieldProps)}
            variant="outlined"
            fullWidth
            placeholder={
              t(
                "step.companyInformation.registrationNumberOrNameLabel.placeholder"
              ) || undefined
            }
            label={t("step.companyInformation.registrationNumberOrNameLabel")}
            error={!!error}
            helperText={error?.message}
          />
        )}
      />
    </Box>
  );
};

export default CompanySearchAutocomplete;
